import { inject, Injectable } from '@angular/core';
import { IUserMe } from '@app/api/interfaces/user-me.interface';
import { NgxPermissionsService } from 'ngx-permissions';
import { PermissionName } from '@app/core/permissions/enums/permission-name.enum';
import { IPermission } from '@app/core/permissions/interfaces/permission.interface';
import { distinctUntilChanged, Subscription } from 'rxjs';
import { UserType } from '@app/core/enums/user-type.enum';
import { GlobalPermissionsService } from '@app/core/permissions/configs/global-permissions.service';
import { IPermissionConfig } from '@app/core/permissions/interfaces/feature-permissions.interface';

@Injectable({
  providedIn: 'root',
})
export class PermissionsService {
  private ngxPermissionsService = inject(NgxPermissionsService);
  private globalPermissionsService = inject(GlobalPermissionsService);

  private _role?: UserType;
  private permissionsLocalStore = new Map<PermissionName, { permission: IPermission; sub?: Subscription }>();

  constructor() {
    // this.ngxPermissionsService.permissions$
    //   .pipe(takeUntilDestroyed(), distinctUntilChanged())
    //   .subscribe(permissions => {
    //     console.log(
    //       '[PERMISSIONS]:',
    //       Object.keys(permissions).filter(key => permissions[key])
    //     );
    //   });
  }

  public initUserPermissions(user: IUserMe) {
    this.destroyUserPermissions();

    this._role = user.type;

    this.activatePermissions(this.globalPermissionsService.CONFIGS);
  }

  public destroyUserPermissions() {
    this.permissionsLocalStore.forEach(entry => entry.sub?.unsubscribe());
    this.permissionsLocalStore.clear();
    this._role = undefined;
    this.ngxPermissionsService.flushPermissions();
  }

  public activatePermissions(permissionConfigs: IPermissionConfig[]) {
    const permissions = this.getRolePermissions(permissionConfigs);

    const staticPermissions = permissions.filter(p => !p.condition$);
    const reactivePermissions = permissions.filter(p => p.condition$);

    if (staticPermissions.length) {
      staticPermissions.forEach(permission => {
        this.permissionsLocalStore.set(permission.name, { permission });
      });
      this.ngxPermissionsService.addPermission(staticPermissions.map(p => p.name));
    }

    reactivePermissions.forEach(permission => this.subscribePermissionCondition(permission));
  }

  public deactivatePermissions(permissionConfigs: IPermissionConfig[]) {
    const permissions = this.getRolePermissions(permissionConfigs);

    permissions.forEach(permission => this.deactivatePermission(permission.name));
  }

  private get role() {
    if (!this._role) {
      console.warn('[PERMISSIONS]: Role is not initialized');
    }

    return this._role;
  }

  private subscribePermissionCondition(permission: IPermission) {
    this.permissionsLocalStore.set(permission.name, { permission });
    const entry = this.permissionsLocalStore.get(permission.name)!;

    entry.sub?.unsubscribe();
    entry.sub = permission.condition$
      ?.pipe(distinctUntilChanged())
      .subscribe(active => this.checkPermissionCondition(permission.name, active));
  }

  private checkPermissionCondition(permissionName: PermissionName, shouldBeActive: boolean) {
    const isActive = !!this.ngxPermissionsService.getPermission(permissionName);

    if (isActive === shouldBeActive) return;
    if (shouldBeActive) {
      this.ngxPermissionsService.addPermission(permissionName);
    } else {
      this.ngxPermissionsService.removePermission(permissionName);
    }
  }

  private deactivatePermission(permissionName: PermissionName) {
    const entry = this.permissionsLocalStore.get(permissionName);

    entry?.sub?.unsubscribe();
    this.permissionsLocalStore.delete(permissionName);

    if (this.ngxPermissionsService.getPermission(permissionName)) {
      this.ngxPermissionsService.removePermission(permissionName);
    }
  }

  private getRolePermissions(permissionConfigs: IPermissionConfig[]): IPermission[] {
    return permissionConfigs.flatMap(config => {
      const role = config.roles.find(r => r.role === this.role);
      if (!role) return [];

      return [
        {
          name: config.name,
          condition$: role.condition$,
        },
      ];
    });
  }
}
